import { computed, ref } from 'vue';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { INTEGRATION_TYPES } from '@/data/onboarding';
import {
  SALES_PLATFORMS,
  MARKETING_PLATFORMS,
  ALL_PLATFORMS,
} from '@/data/platforms';
import { CONNECTION_STATUS } from '@/data/connection-types';
import {
  getExtAccountStatus,
  getShopifyAccStatus,
  getShopifyNativeAccStatus,
} from '@/utils/external-accounts';
import { ContextRoot } from '@/utils/context-root';
import { ExternalAccount } from '@/types/external-account.types';

const eventKeys = {
  [INTEGRATION_TYPES.SALES]: {
    attempt: SEGMENT_EVENTS.fe_sales_external_conn_attempted,
    failed: SEGMENT_EVENTS.fe_sales_account_failed_connect,
    connected: SEGMENT_EVENTS.fe_sales_account_connected,
    openModal: SEGMENT_EVENTS.fe_sales_account_modal_open,
    closeModal: SEGMENT_EVENTS.fe_sales_account_modal_close,
  },
  [INTEGRATION_TYPES.MARKETING]: {
    attempt: SEGMENT_EVENTS.fe_marketing_external_conn_attempted,
    failed: SEGMENT_EVENTS.fe_marketing_account_failed_connect,
    connected: SEGMENT_EVENTS.fe_marketing_account_connected,
    openModal: SEGMENT_EVENTS.fe_marketing_account_modal_open,
    closeModal: SEGMENT_EVENTS.fe_marketing_account_modal_close,
  },
  [INTEGRATION_TYPES.ACCOUNTING]: {
    attempt: SEGMENT_EVENTS.fe_accounting_external_conn_attempted,
    failed: SEGMENT_EVENTS.fe_accounting_account_failed_connect,
    connected: SEGMENT_EVENTS.fe_accounting_account_connected,
    openModal: SEGMENT_EVENTS.fe_accounting_account_modal_open,
    closeModal: SEGMENT_EVENTS.fe_accounting_account_modal_close,
  },
};

export default function (context: ContextRoot) {
  const ctx = context.root;
  const { getters } = ctx.$store;

  const initialErrors = Object.values({
    ...SALES_PLATFORMS,
    ...MARKETING_PLATFORMS,
  }).reduce((acc, value) => {
    acc[value] = false;
    return acc;
  }, {} as any);

  const userId = computed<number>(() => getters.userId);
  const businessId = computed<number>(() => getters.businessId);
  const externalAccounts = computed<ExternalAccount[]>(
    () => getters.externalAccounts,
  );
  const showShopifyFivetranTile = computed<boolean>(
    () => getters.showShopifyFivetranTile,
  );
  const showNextStepsError = ref(false);
  const errors = ref(initialErrors);

  const getIntegrationType = (platformName: string) => {
    if (Object.values(SALES_PLATFORMS).includes(platformName as any)) {
      return INTEGRATION_TYPES.SALES;
    } else if (
      Object.values(MARKETING_PLATFORMS).includes(platformName as any)
    ) {
      return INTEGRATION_TYPES.MARKETING;
    }
    return INTEGRATION_TYPES.ACCOUNTING;
  };

  const resetError = () => {
    ctx.$store.commit('CLEAR_REQUEST_STATUS', 'ADD_CONNECTED_ACCOUNT');
    ctx.$store.commit('CLEAR_REQUEST_STATUS', 'OAUTH_CONNECT');
    showNextStepsError.value = false;
    errors.value = initialErrors;
  };

  const trackSeeMoreClick = (props?: any) => {
    analytics.track(SEGMENT_EVENTS.fe_see_more_click, props);
  };

  const trackAccountConnected = (platform: string, props?: any) => {
    const eventName = eventKeys[getIntegrationType(platform)].connected;
    analytics.track(eventName, {
      category: 'Connect',
      platform,
      userId: userId.value,
      businessId: businessId.value,
      integrationType: getIntegrationType(platform),
      ...props,
    });

    analytics.track(SEGMENT_EVENTS.if_marketing_account_connected_success, {
      platform,
      businessId: businessId.value,
    });
  };

  const trackRedirect = (platformName: string, props?: any) => {
    resetError();
    const eventName = eventKeys[getIntegrationType(platformName)].attempt;
    analytics.track(eventName, {
      platform: platformName,
      ...props,
    });
  };

  const updateTileToErrorStatus = (platformName: string) => {
    showNextStepsError.value = true;
    errors.value[platformName] = true;
  };
  const handleTileClick = (platformName: string, props?: any) => {
    const integrationType = getIntegrationType(platformName);
    resetError();
    analytics.track(SEGMENT_EVENTS.fe_integration_click, {
      platform: platformName,
      integrationType,
      ...props,
    });
    if (integrationType === INTEGRATION_TYPES.ACCOUNTING) {
      trackRedirect(platformName, props);
    }
  };
  const handleOauthError = (payload: any, props?: any) => {
    updateTileToErrorStatus(payload.provider);
    const eventName = eventKeys[getIntegrationType(payload.provider)].failed;
    analytics.track(eventName, {
      ...props,
      platform: payload.provider,
      errorMsg: payload.error,
    });
  };

  const trackErrorBanner = (
    action: string,
    props?: any,
    errorBannerMessage?: string,
  ) => {
    if (action === 'visible') {
      analytics.track(SEGMENT_EVENTS.fe_error_banner_displayed, {
        bannerCopy: errorBannerMessage,
        ...props,
      });
    } else if (action === 'email_click') {
      analytics.track(SEGMENT_EVENTS.fe_error_banner_email_clicked, props);
    } else if (action === 'phone_click') {
      analytics.track(SEGMENT_EVENTS.fe_error_banner_phone_clicked, props);
    }
  };

  const trackNewPlatformModal = (
    props: any,
    action: string,
    integrationType: INTEGRATION_TYPES,
    requestedPlatform: string,
  ) => {
    if (['submitted', 'entered'].includes(action)) {
      const eventName =
        action === 'submitted'
          ? SEGMENT_EVENTS.fe_selfserve_platform_requested
          : SEGMENT_EVENTS.fe_platform_requested_name_entered;
      analytics.track(eventName, {
        ...props,
        requestedPlatform,
        integrationType,
      });
    } else {
      analytics.track(SEGMENT_EVENTS.fe_no_platform_click, {
        ...props,
        integrationType,
      });
    }
  };

  const trackModal = (
    platformName: string,
    modalAction: 'open' | 'close',
    props?: any,
    isMobile?: boolean,
  ) => {
    const modalType = isMobile ? 'redirect' : 'popup'; // always popups on desktop and redirect on mobile

    // have to click tile to open modal so track that event too
    if (modalAction === 'open') {
      handleTileClick(platformName, props);
    }

    const event = eventKeys[getIntegrationType(platformName)];
    const eventName =
      modalAction === 'open' ? event.openModal : event.closeModal;
    analytics.track(eventName, {
      platform: platformName,
      ...props,
      modalType,
    });
  };

  const getExtAccStatus = (acc: ALL_PLATFORMS) => {
    const account = externalAccounts.value?.find(
      (ext: any) => ext.id.includes(acc) && !ext.ignoreRevenueAt,
    );
    return account && account.state;
  };

  const getActiveExtAccountsByPlatform = (acc: ALL_PLATFORMS) => {
    return (
      externalAccounts.value?.filter(
        (ext: any) => ext.id.includes(acc) && !ext.ignoreRevenueAt,
      ) ?? []
    );
  };

  const isFullyConnected = (acc: ALL_PLATFORMS) => {
    const extAccounts = getActiveExtAccountsByPlatform(acc);
    return (
      !!extAccounts.length &&
      extAccounts.every((ext: any) => {
        if (
          getters.isUseShopifyNativeEnabled &&
          acc === SALES_PLATFORMS.SHOPIFY
        ) {
          return getShopifyNativeAccStatus(ext) === CONNECTION_STATUS.CONNECTED;
        }
        return (
          getExtAccountStatus(ext, showShopifyFivetranTile.value) ===
          CONNECTION_STATUS.CONNECTED
        );
      })
    );
  };

  const hasSomeByStatus = (acc: ALL_PLATFORMS, status: CONNECTION_STATUS) => {
    const extAccounts = getActiveExtAccountsByPlatform(acc);
    return extAccounts.some((ext: any) => {
      if (
        getters.isUseShopifyNativeEnabled &&
        acc === SALES_PLATFORMS.SHOPIFY
      ) {
        return getShopifyNativeAccStatus(ext) === status;
      }

      return getExtAccountStatus(ext, showShopifyFivetranTile.value) === status;
    });
  };

  const hasPendingConnection = (acc: ALL_PLATFORMS) => {
    return hasSomeByStatus(acc, CONNECTION_STATUS.DISCONNECTED);
  };

  const hasProcessingConnection = (acc: ALL_PLATFORMS) => {
    return hasSomeByStatus(acc, CONNECTION_STATUS.PROCESSING);
  };

  const hasWarningConnection = (acc: ALL_PLATFORMS) => {
    return hasSomeByStatus(acc, CONNECTION_STATUS.WARNING);
  };

  return {
    showNextStepsError,
    errors,
    getIntegrationType,
    trackSeeMoreClick,
    trackAccountConnected,
    trackRedirect,
    updateTileToErrorStatus,
    handleTileClick,
    handleOauthError,
    trackErrorBanner,
    trackNewPlatformModal,
    trackModal,
    getExtAccStatus,
    hasPendingConnection,
    isFullyConnected,
    hasProcessingConnection,
    hasWarningConnection,
    getShopifyAccStatus,
  };
}
